import type { ConversationDTO, ConversationMessagesDTO, RenameConversationDTO } from '@shared/schemas/chat';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import {
  deleteConversation,
  getConversation,
  listConversations,
  renameConversation,
  updateConversationRagMode,
  updateConversationWebSearchMode,
} from '../services/chat.api';
import type { ConversationListResponse } from '../types';

export const conversationKeys = {
  all: ['conversations'] as const,
  detail: (conversationId: string) => [...conversationKeys.all, 'detail', conversationId] as const,
  infiniteLists: () => [...conversationKeys.all, 'infinite-list'] as const,
  list: (page: number, pageSize: number) => [...conversationKeys.all, 'list', { page, pageSize }] as const,
  lists: () => [...conversationKeys.all, 'list'] as const,
};

type ConversationListSnapshot = Array<[QueryKey, ConversationListResponse | undefined]>;
type InfiniteConversationListSnapshot = Array<[QueryKey, InfiniteData<ConversationListResponse> | undefined]>;

type MutationCacheContext = {
  previousDetail: ConversationMessagesDTO | undefined;
  previousDetailKey: QueryKey;
  previousInfiniteLists: InfiniteConversationListSnapshot;
  previousLists: ConversationListSnapshot;
};

function removeConversationFromListResponse(
  response: ConversationListResponse | undefined,
  conversationId: string,
): ConversationListResponse | undefined {
  if (!response) {
    return response;
  }

  const nextItems = response.items.filter((conversation) => conversation.id !== conversationId);

  if (nextItems.length === response.items.length) {
    return response;
  }

  return {
    ...response,
    items: nextItems,
    meta: {
      ...response.meta,
      total: Math.max(response.meta.total - 1, 0),
    },
  };
}

function renameConversationInListResponse(
  response: ConversationListResponse | undefined,
  conversation: ConversationDTO,
): ConversationListResponse | undefined {
  if (!response) {
    return response;
  }

  let hasChanged = false;
  const nextItems = response.items.map((item) => {
    if (item.id !== conversation.id) {
      return item;
    }

    hasChanged = true;
    return {
      ...item,
      title: conversation.title,
      updatedAt: conversation.updatedAt,
    };
  });

  return hasChanged
    ? {
      ...response,
      items: nextItems,
    }
    : response;
}

function removeConversationFromInfiniteListResponse(
  response: InfiniteData<ConversationListResponse> | undefined,
  conversationId: string,
): InfiniteData<ConversationListResponse> | undefined {
  if (!response) {
    return response;
  }

  let hasChanged = false;
  const nextPages: ConversationListResponse[] = response.pages.map((page) => {
    const nextPage = removeConversationFromListResponse(page, conversationId);

    if (nextPage !== page) {
      hasChanged = true;
    }

    return nextPage ?? page;
  });

  return hasChanged
    ? {
      ...response,
      pages: nextPages,
    }
    : response;
}

function renameConversationInInfiniteListResponse(
  response: InfiniteData<ConversationListResponse> | undefined,
  conversation: ConversationDTO,
): InfiniteData<ConversationListResponse> | undefined {
  if (!response) {
    return response;
  }

  let hasChanged = false;
  const nextPages: ConversationListResponse[] = response.pages.map((page) => {
    const nextPage = renameConversationInListResponse(page, conversation);

    if (nextPage !== page) {
      hasChanged = true;
    }

    return nextPage ?? page;
  });

  return hasChanged
    ? {
      ...response,
      pages: nextPages,
    }
    : response;
}

function renameConversationInDetailResponse(
  response: ConversationMessagesDTO | undefined,
  conversation: ConversationDTO,
) {
  if (!response || response.conversation.id !== conversation.id) {
    return response;
  }

  return {
    ...response,
    conversation: {
      ...response.conversation,
      title: conversation.title,
      updatedAt: conversation.updatedAt,
    },
  };
}

function findConversationInSnapshots(
  snapshots: ConversationListSnapshot,
  conversationId: string,
) {
  for (const [, snapshot] of snapshots) {
    const conversation = snapshot?.items.find((item) => item.id === conversationId);

    if (conversation) {
      return conversation;
    }
  }

  return null;
}

function restoreConversationCacheSnapshots(
  queryClient: QueryClient,
  context: MutationCacheContext,
) {
  context.previousLists.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });

  context.previousInfiniteLists.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });

  if (context.previousDetail) {
    queryClient.setQueryData(context.previousDetailKey, context.previousDetail);
    return;
  }

  queryClient.removeQueries({ exact: true, queryKey: context.previousDetailKey });
}

export function useConversation(conversationId?: string) {
  return useQuery({
    enabled: Boolean(conversationId),
    queryFn: () => getConversation(conversationId!),
    queryKey: conversationId ? conversationKeys.detail(conversationId) : [...conversationKeys.all, 'detail', 'empty'],
  });
}

export function useConversationsList(page = 1, pageSize = 20) {
  return useQuery({
    queryFn: () => listConversations(page, pageSize),
    queryKey: conversationKeys.list(page, pageSize),
  });
}

export function useInfiniteConversationsList(pageSize = 20) {
  return useInfiniteQuery<Awaited<ReturnType<typeof listConversations>>, Error>({
    getNextPageParam: (lastPage) => (
      lastPage.meta.page < lastPage.meta.pageCount ? lastPage.meta.page + 1 : undefined
    ),
    initialPageParam: 1,
    queryFn: ({ pageParam }) => listConversations(Number(pageParam), pageSize),
    queryKey: [...conversationKeys.infiniteLists(), pageSize],
  });
}

export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    onError: (_error, _conversationId, context) => {
      if (!context) {
        return;
      }

      restoreConversationCacheSnapshots(queryClient, context);
    },
    onMutate: async (conversationId: string) => {
      const detailKey = conversationKeys.detail(conversationId);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: conversationKeys.all }),
        queryClient.cancelQueries({ exact: true, queryKey: detailKey }),
      ]);

      const previousDetail = queryClient.getQueryData<ConversationMessagesDTO>(detailKey);
      const previousLists = queryClient.getQueriesData<ConversationListResponse>({ queryKey: conversationKeys.lists() });
      const previousInfiniteLists = queryClient.getQueriesData<InfiniteData<ConversationListResponse>>({
        queryKey: conversationKeys.infiniteLists(),
      });

      queryClient.removeQueries({ exact: true, queryKey: detailKey });
      queryClient.setQueriesData<ConversationListResponse>(
        { queryKey: conversationKeys.lists() },
        (current) => removeConversationFromListResponse(current, conversationId),
      );
      queryClient.setQueriesData<InfiniteData<ConversationListResponse>>(
        { queryKey: conversationKeys.infiniteLists() },
        (current) => removeConversationFromInfiniteListResponse(current, conversationId),
      );

      return {
        previousDetail,
        previousDetailKey: detailKey,
        previousInfiniteLists,
        previousLists,
      } satisfies MutationCacheContext;
    },
    mutationFn: (conversationId: string) => deleteConversation(conversationId),
    onSuccess: (_payload, conversationId) => {
      queryClient.removeQueries({ exact: true, queryKey: conversationKeys.detail(conversationId) });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
  });
}

export function useRenameConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      restoreConversationCacheSnapshots(queryClient, context);
    },
    onMutate: async ({ conversationId, input }: { conversationId: string; input: RenameConversationDTO }) => {
      const detailKey = conversationKeys.detail(conversationId);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: conversationKeys.all }),
        queryClient.cancelQueries({ exact: true, queryKey: detailKey }),
      ]);

      const previousDetail = queryClient.getQueryData<ConversationMessagesDTO>(detailKey);
      const previousLists = queryClient.getQueriesData<ConversationListResponse>({ queryKey: conversationKeys.lists() });
      const previousInfiniteLists = queryClient.getQueriesData<InfiniteData<ConversationListResponse>>({
        queryKey: conversationKeys.infiniteLists(),
      });

      const baseConversation = previousDetail?.conversation ?? findConversationInSnapshots(previousLists, conversationId);
      const optimisticConversation: ConversationDTO | null = baseConversation
        ? {
          ...baseConversation,
          title: input.title,
        }
        : null;

      if (optimisticConversation) {
        queryClient.setQueryData<ConversationMessagesDTO>(
          detailKey,
          renameConversationInDetailResponse(previousDetail, optimisticConversation),
        );
        queryClient.setQueriesData<ConversationListResponse>(
          { queryKey: conversationKeys.lists() },
          (current) => renameConversationInListResponse(current, optimisticConversation),
        );
        queryClient.setQueriesData<InfiniteData<ConversationListResponse>>(
          { queryKey: conversationKeys.infiniteLists() },
          (current) => renameConversationInInfiniteListResponse(current, optimisticConversation),
        );
      }

      return {
        previousDetail,
        previousDetailKey: detailKey,
        previousInfiniteLists,
        previousLists,
      } satisfies MutationCacheContext;
    },
    mutationFn: ({ conversationId, input }: { conversationId: string; input: RenameConversationDTO }) => (
      renameConversation(conversationId, input)
    ),
    onSuccess: (conversation) => {
      queryClient.setQueryData<ConversationMessagesDTO>(
        conversationKeys.detail(conversation.id),
        (current) => renameConversationInDetailResponse(current, conversation),
      );
      queryClient.setQueriesData<ConversationListResponse>(
        { queryKey: conversationKeys.lists() },
        (current) => renameConversationInListResponse(current, conversation),
      );
      queryClient.setQueriesData<InfiniteData<ConversationListResponse>>(
        { queryKey: conversationKeys.infiniteLists() },
        (current) => renameConversationInInfiniteListResponse(current, conversation),
      );
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: conversationKeys.all }),
        queryClient.invalidateQueries({ queryKey: conversationKeys.detail(conversation.id) }),
      ]);
    },
  });
}

export function useUpdateConversationRagMode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, ragEnabled }: { conversationId: string; ragEnabled: boolean }) => (
      updateConversationRagMode(conversationId, { ragEnabled })
    ),
    onSuccess: (conversation) => {
      queryClient.setQueryData<ConversationMessagesDTO>(
        conversationKeys.detail(conversation.id),
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            conversation: {
              ...current.conversation,
              ragEnabled: conversation.ragEnabled,
              updatedAt: conversation.updatedAt,
            },
          };
        },
      );
      void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.detail(conversation.id) });
    },
  });
}

export function useUpdateConversationWebSearchMode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, webSearchEnabled }: { conversationId: string; webSearchEnabled: boolean }) => (
      updateConversationWebSearchMode(conversationId, { webSearchEnabled })
    ),
    onSuccess: (conversation) => {
      queryClient.setQueryData<ConversationMessagesDTO>(
        conversationKeys.detail(conversation.id),
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            conversation: {
              ...current.conversation,
              updatedAt: conversation.updatedAt,
              webSearchEnabled: conversation.webSearchEnabled,
            },
          };
        },
      );
      void queryClient.invalidateQueries({ queryKey: conversationKeys.all });
      void queryClient.invalidateQueries({ queryKey: conversationKeys.detail(conversation.id) });
    },
  });
}
